import React, { useState } from 'react';

import { kebabCase } from 'lodash-es';

import { Button, Link } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { getTrackingDataForTask } from 'src/app/pages/starterDashboardV2/shared/hooks';
import { Waypoint } from 'src/app/shared/components/scroll';
import { useTrackOnce } from 'src/app/shared/hooks/tracking';

import type { TaskShape } from '../../../../types';
import { TaskCheckbox } from '../../../DynamicTasks/components/TaskCheckbox';

import { activeTaskStyles } from './ActiveTask.styles';
import { TaskTextContent } from './TaskTextContent';

type ActiveTaskActionButtonProps = Readonly<{
	primaryActionHref?: string;
	primaryActionText: string;
	primaryActionOnClick?: () => void;
	isFirstActive: boolean;
	name: string;
}>;

const ActiveTaskActionButton: React.FC<ActiveTaskActionButtonProps> = ({
	primaryActionHref,
	primaryActionText,
	primaryActionOnClick,
	isFirstActive,
	name,
}) => {
	const variant: 'primary' | 'secondary' = isFirstActive ? 'primary' : 'secondary';
	const dataQa = `task-action-button-${name}`;
	const buttonClassName = 'bg-fill-inverse-strong w-full whitespace-nowrap';
	if (primaryActionHref) {
		return (
			<Button
				as="a"
				href={primaryActionHref}
				variant={variant}
				size="x-small"
				onClick={primaryActionOnClick}
				data-qa={dataQa}
				className={buttonClassName}
			>
				{primaryActionText}
			</Button>
		);
	}
	return (
		<Button
			variant={variant}
			size="x-small"
			onClick={primaryActionOnClick}
			data-qa={dataQa}
			className={buttonClassName}
		>
			{primaryActionText}
		</Button>
	);
};

type ActiveTaskProps = Omit<TaskShape, 'status'>;

export const ActiveTask: React.FC<ActiveTaskProps> = ({
	name,
	title,
	description,
	primaryActionText,
	primaryActionHref,
	primaryActionOnClick,
	onCheckboxChange,
	isFirstActive = false,
}) => {
	const trackOnce = useTrackOnce();
	const [isChecked, setIsChecked] = useState(false);

	const handleCheckboxChange = (newValue: boolean) => {
		if (newValue) {
			setIsChecked(true);
			void onCheckboxChange?.();
		}
	};

	const styles = activeTaskStyles();

	return (
		<>
			<Waypoint
				onEnter={async () =>
					trackOnce('petition_manage_tasks_view', {
						...getTrackingDataForTask(name),
						version: 'new_12_2025',
					})
				}
			/>
			<div data-qa="petition-task" className="flex flex-col gap-3 py-5 md:contents">
				<div
					className={cn(
						styles.checkboxWrapper(),
						'md:border-stroke-weaker md:col-start-1 md:row-start-1 md:border-b md:py-5 md:pr-6',
					)}
				>
					<TaskCheckbox
						name={`task-checkbox-${kebabCase(name)}`}
						isSelected={isChecked}
						isReadOnly={!!isChecked}
						onChange={handleCheckboxChange}
						ariaLabel={title}
					/>
					{primaryActionHref ? (
						<Link
							href={primaryActionHref}
							onClick={primaryActionOnClick}
							data-qa={`task-text-link-${kebabCase(name)}`}
							className="flex-1 text-left"
						>
							<TaskTextContent name={name} title={title} description={description} />
						</Link>
					) : (
						<button
							type="button"
							onClick={primaryActionOnClick}
							data-qa={`task-text-button-${kebabCase(name)}`}
							className="flex-1 text-left"
						>
							<TaskTextContent name={name} title={title} description={description} />
						</button>
					)}
				</div>
				<div className="md:border-stroke-weaker flex items-center md:col-start-2 md:row-start-1 md:border-b md:py-5">
					<ActiveTaskActionButton
						primaryActionHref={primaryActionHref}
						primaryActionText={primaryActionText}
						primaryActionOnClick={primaryActionOnClick}
						isFirstActive={isFirstActive}
						name={kebabCase(name)}
					/>
				</div>
			</div>
		</>
	);
};
